import { ProjectState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

export const getProjectStateLabel = (state: ProjectState): string =>
  t(`project.state.${state}`);

export enum ProjectsView {
  PROJECTS = 'projects',
  QUOTES = 'quotes',
}

export enum ProjectsURLParams {
  TAB = 'tab',
  VIEW = 'view',
}

export enum ProjectsStorageKeys {
  TAB_PARAM = 'projects_v2_tab',
  VIEW_PARAM = 'projects_v2_view',
}

export const QUOTE_PROJECT_STATES: readonly ProjectState[] = [
  ProjectState.DRAFT,
  ProjectState.QUOTED,
  ProjectState.LOST,
] as const;

export const ACTIVE_PROJECT_STATES: readonly ProjectState[] = [
  ProjectState.ACTIVE,
  ProjectState.COMPLETED,
  ProjectState.INACTIVE,
  ProjectState.CANCELED,
] as const;

export const DEFAULT_PROJECT_VIEW = ProjectsView.PROJECTS;

const PROJECT_VIEW_BY_STATE: Record<ProjectState, ProjectsView> = {
  [ProjectState.DRAFT]: ProjectsView.QUOTES,
  [ProjectState.QUOTED]: ProjectsView.QUOTES,
  [ProjectState.LOST]: ProjectsView.QUOTES,
  [ProjectState.ACTIVE]: ProjectsView.PROJECTS,
  [ProjectState.COMPLETED]: ProjectsView.PROJECTS,
  [ProjectState.INACTIVE]: ProjectsView.PROJECTS,
  [ProjectState.CANCELED]: ProjectsView.PROJECTS,
};

export const PROJECT_STATE_TABS_BY_VIEW: Record<ProjectsView, readonly ProjectState[]> = {
  [ProjectsView.QUOTES]: QUOTE_PROJECT_STATES,
  [ProjectsView.PROJECTS]: ACTIVE_PROJECT_STATES,
};

export const isValidProjectsView = (
  value: string | null | undefined,
): value is ProjectsView => Object.values(ProjectsView).includes(value as ProjectsView);

export const isValidProjectState = (
  value: string | null | undefined,
): value is ProjectState => Object.values(ProjectState).includes(value as ProjectState);

export const isProjectStateInView = (view: ProjectsView, state: ProjectState): boolean =>
  PROJECT_STATE_TABS_BY_VIEW[view].includes(state);

export const getDefaultProjectStateForView = (view: ProjectsView): ProjectState =>
  PROJECT_STATE_TABS_BY_VIEW[view][0];

export const getViewForState = (state: ProjectState): ProjectsView =>
  PROJECT_VIEW_BY_STATE[state];

export const getInitialProjectsView = (searchParams: URLSearchParams): ProjectsView => {
  const fromUrl = searchParams.get(ProjectsURLParams.VIEW);
  const fromStorage = localStorage.getItem(ProjectsStorageKeys.VIEW_PARAM);
  const candidate = fromUrl ?? fromStorage ?? '';

  return isValidProjectsView(candidate) ? candidate : DEFAULT_PROJECT_VIEW;
};

export const getInitialProjectState = (
  searchParams: URLSearchParams,
  view: ProjectsView,
): ProjectState => {
  const fromUrl = searchParams.get(ProjectsURLParams.TAB);
  const fromStorage = localStorage.getItem(ProjectsStorageKeys.TAB_PARAM);
  const candidate = fromUrl ?? fromStorage ?? '';

  if (isValidProjectState(candidate) && isProjectStateInView(view, candidate)) {
    return candidate;
  }

  return getDefaultProjectStateForView(view);
};
